import * as React from 'react';
import { cx } from 'linaria';

import { useStableCallback, noop, fadeIn, makeTestId } from '../';

import * as styles from './image.styles';

interface ImageOwnProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    /**
     *  Source of the image
     */
    src: string;
    /**
     *  Alternate text for the image
     */
    alt: string;
    /**
     * CSS Object fit for the image
     * @default cover
     */
    fit?: 'cover' | 'contain' | 'scale-down';
    /**
     *  Priority hints for the image
     *  @default lazy
     */
    loading?: 'eager' | 'lazy';
    /**
     *  Priority  decoding hint to the browser
     *  @default async
     */
    decoding?: 'sync' | 'async' | 'auto';
    /**
     *  Fallback content to be displayed when there is an error or loading
     */
    fallbackSlot?: (status: Status) => React.ReactElement;
    /**
     *  Prominent color of the image
     */
    color?: string;
    /**
     *  Success callback handler
     */
    onLoad?: () => void;
    /**
     *  Error callback handler
     */
    onError?: () => void;
    /**
     *  Enable/Disable fading animation
     *  @default true
     */
    animation?: boolean;
    /**
     *  Test id for unit and e2e
     */
    testId?: string;
}

interface ImageSizeProps extends ImageOwnProps {
    /**
     *  Height of the image
     */
    width: number | 'full';
    /**
     *  Width of the image
     */
    height: number | 'full';
    aspectRatio?: never;
}

interface ImageAspectRatioProps extends ImageOwnProps {
    /**
     *  Aspect ratio of the image container to prevent layout shifts
     *  @default 1
     */
    aspectRatio?: number;
    height?: never;
    width?: never;
}

export type ImageProps = ImageSizeProps | ImageAspectRatioProps;

enum Status {
    Idle = 'idle',
    Loading = 'loading',
    Error = 'error',
    Success = 'success',
}

/**
 *  A component to display images in the page with sensible default config. Main features are [**browser native lazy loading**](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading),
 *  height/width or aspect-ratio based sizing to **prevent layout shifts** and
 *  [**async decoding**](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-decoding).
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>((props, ref) => {
    const {
        src,
        loading = 'lazy',
        decoding = 'async',
        fit = 'cover',
        alt,
        width,
        height,
        aspectRatio = 1,
        animation = true,
        color,
        onLoad: propOnLoad = noop,
        onError: propOnError = noop,
        fallbackSlot = () => null,
        testId,
        ...passThroughProps
    } = props;

    const [status, setStatus] = React.useState(Status.Idle);
    const onLoad = useStableCallback(propOnLoad);
    const onError = useStableCallback(propOnError);

    React.useEffect(() => {
        function loadImage() {
            if (src == null || (typeof src === 'string' && src.trim().length === 0)) {
                setStatus(Status.Error);
                return;
            }

            setStatus(Status.Loading);

            const image = new window.Image();
            image.src = src;

            image.onload = () => {
                setStatus(Status.Success);
                onLoad?.();
            };

            image.onerror = () => {
                setStatus(Status.Error);
                onError?.();
            };
        }

        loadImage();
    }, [src]);

    const hasSize = width != null && height != null;

    const isFullHeight = height === 'full';
    const isFullWidth = width === 'full';

    const wrapperStyle = React.useMemo(() => {
        const styles: React.CSSProperties = {};

        if (!isFullHeight) {
            styles.height = height;
        }

        if (!isFullWidth) {
            styles.width = width;
        }

        if (!hasSize) {
            styles.height = 0;
            styles.paddingBottom = `${100 / aspectRatio}%`;
        }

        return styles;
    }, [isFullHeight, isFullWidth, width, height, hasSize, aspectRatio]);

    return (
        <div
            {...passThroughProps}
            title={alt}
            className={cx(
                styles.wrapper,
                isFullHeight && styles.fullHeight,
                isFullWidth && styles.fullWidth,
                passThroughProps.className,
            )}
            data-testid={testId}
            style={{
                backgroundColor: color,
                ...wrapperStyle,
                ...passThroughProps.style,
            }}
        >
            {status === Status.Success ? (
                <img
                    className={cx(styles.image, styles.fitVariants[fit], animation && fadeIn)}
                    ref={ref}
                    alt={alt}
                    src={src}
                    decoding={decoding}
                    loading={loading}
                    data-testid={makeTestId(testId, 'image')}
                />
            ) : (
                <div className={styles.fallback} data-testid={makeTestId(testId, 'fallback')}>
                    {fallbackSlot(status)}
                </div>
            )}
        </div>
    );
});

Image.displayName = 'Image';
